import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router';

import {
  IonToolbar,
  IonContent,
  IonPage,
  IonTitle,
  IonMenuButton,
  IonButton,
  IonButtons,
  IonHeader,
  IonList,
  IonListHeader,
  IonItem,
  IonInput,
  IonLoading,
  getConfig,
  useIonToast,
} from '@ionic/react';

import './ShipmentPage.scss';
import { connect } from '../data/connect';
import { Shipment } from '../models/Shipment';
import ShipmentList from '../components/ShipmentList';
import ShipmentCard from '../components/ShipmentCard';
import axiosInstance from '../data/axios/axiosInstance';

interface OwnProps {
  onTabChange: (event: CustomEvent) => void;
}

interface StateProps {
  mode: 'ios' | 'md';
  isAuthenticated: boolean;
}

interface DispatchProps {
}

type ShipmentPageProps = OwnProps & StateProps & DispatchProps;

const ShipmentPage: React.FC<ShipmentPageProps> = ({
  isAuthenticated,
  onTabChange,
}) => {
  const [barcode, setBarcode] = useState('');
  const [shipment, setShipment] = useState<Shipment>();
  const [count, setCount] = useState(0);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [segment, setSegment] = useState("shipment");
  const [loading, setLoading] = useState(false);
  const pageRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLIonInputElement>(null);
  const [presentToast] = useIonToast();
  const location = useLocation();

  const showToast = (message: string, duration = 3000) => {
    presentToast({
      message,
      duration,
      position: 'middle',
      buttons: [
        {
          text: 'Close',
          role: 'cancel',
          handler: () => {
            // Close 押下後にフォーカスを戻す
            setTimeout(() => {
              inputRef.current?.setFocus();
            }, 100);
          },               
        },
      ],
    });
  };

  useEffect(() => {
    inputRef.current?.setFocus();
  }, [shipment]);

  const playBuzzer = () => {
    const buzzer = document.getElementById('buzzer') as HTMLAudioElement;
    if (buzzer) {
      buzzer.play();
    }
  };

  const playRing = () => {
    const ring = document.getElementById('ring') as HTMLAudioElement;
    if (ring) {
      ring.play();
    }
  };

  const fetchShipments = async () => {
    try {
      const response = await axiosInstance.get('/shipment');
      if (response.status === 200) {
        setCount(response.data.count)
        setShipment(response.data.data)
      }
    } catch (error) {
      console.error('Error fetching shipments:', error);
    }
  };

  const handleBarcodeChange = (event: CustomEvent) => {
    setBarcode((event.target as HTMLInputElement).value.toUpperCase());
  };

  const handleKeyPress = async (event: React.KeyboardEvent<HTMLIonInputElement>) => {
    if (event.key === 'Enter') {
      if ( barcode !== "" && (barcode === shipment?.jan || barcode === shipment?.original_num) ) {
        setLoading(true);
        try {
          const response = await axiosInstance.post('/shipments/', { barcode: barcode });
          if (response.status === 200) {
            if (response.data.result !== "success") {
              setCount(response.data.count);
              setShipment(response.data.shipment);
            } else if (response.data.result === "success") {
              setSegment("success");
              setShipments(response.data.shipments);
            }
            setBarcode("");
            setLoading(false);     
            playRing();
          } else {
            playBuzzer();
          }
        } catch (error) {
          console.error('Error processing shipments:', error);
          setBarcode("");
          setLoading(false);
          playBuzzer();
        }
      } else {
        setBarcode("");
        showToast("バーコード入力エラー");
        inputRef.current?.setFocus();
        playBuzzer();
      }
    }
  };

  const handleSearch = async () => {
    if ( barcode !== "" && (barcode === shipment?.jan || barcode === shipment?.original_num) ) {
      setLoading(true);
      try {
        const response = await axiosInstance.post('/shipments/', { barcode: barcode });
        if (response.status === 200) {
          if (response.data.result !== "success") {
            setCount(response.data.count);
            setShipment(response.data.shipment);
          } else if (response.data.result === "success") {
            setSegment("success");
            setShipments(response.data.shipments);
          }
          setBarcode("");
          setLoading(false);     
          playRing();
        } else {
          playBuzzer();
        }
      } catch (error) {
        console.error('Error processing shipments:', error);
        setBarcode("");
        setLoading(false);
        playBuzzer();
      }
    } else {
      setBarcode("");
      showToast("バーコード入力エラー");
      inputRef.current?.setFocus();
      playBuzzer();
    }
  };

  const handleClear = () => {
    setBarcode("");
    inputRef.current?.setFocus();
  }
  
  useEffect(() => {
    if (isAuthenticated && location.pathname.includes("shipment")) {
      fetchShipments();
    }
  }, [isAuthenticated, onTabChange]);

  return (
    <IonPage ref={pageRef} id="shipment-page">
      <IonHeader translucent={true}>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>出庫作業</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen={true}>
        <audio id="buzzer" src="/assets/error.mp3" preload="auto"></audio>
        <audio id="ring" src="/assets/ok.mp3" preload="auto"></audio>
        <IonList>
          <IonItem>
            <IonInput label="バーコード" ref={inputRef} placeholder="0000000000000" value={barcode} onIonInput={handleBarcodeChange} onKeyDown={handleKeyPress}></IonInput>
            <IonButton color="primary" expand="full" slot='end' onClick={handleClear}>クリア</IonButton>
            <IonButton color="primary" expand="full" slot='end' onClick={handleSearch}>検　索</IonButton>
          </IonItem>
        </IonList>
        <IonListHeader style={{ fontWeight: 'bold', fontSize: '22px' }}>{segment === 'shipment' ? "出庫マスタ : " + count + "件" : "出庫作業完了"}</IonListHeader>
        <ShipmentCard
          shipment={shipment}
          hide={segment === 'success'}
        />
        <div style={{ overflowY: 'auto', height: 'calc(100% - 110px)', display: segment === 'shipment' ? 'none' : '' }}>
          <ShipmentList
            shipments={shipments}
          />
        </div>
        <IonLoading
          isOpen={loading}
          message={'Loading...'}
          onDidDismiss={() => setLoading(false)}
        />
      </IonContent>
    </IonPage>
  );
};

export default connect<OwnProps, StateProps, DispatchProps>({
  mapStateToProps: (state) => ({
    mode: getConfig()!.get('mode'),
    isAuthenticated: state.user.isLoggedin,
  }),
  mapDispatchToProps: {
  },
  component: React.memo(ShipmentPage),
});
